import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { I18nContext, type Translator } from 'i18n/i18n-context';
import { ConfirmModal } from 'shared/modals/confirm-modal/confirm-modal';

export interface ConfirmOptions {
    okButtonText?: string;
    cancelButtonText?: string;
    /** Visible heading; without it the dialog is named by the generic "Confirm". */
    title?: string;
}

interface ConfirmDialogProps {
    message?: React.ReactNode;
    options: Required<Pick<ConfirmOptions, 'okButtonText' | 'cancelButtonText'>> & Pick<ConfirmOptions, 'title'>;
    ariaLabel: string;
    onSettle: (confirmed: boolean) => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ message, options, ariaLabel, onSettle }) => (
    <ConfirmModal
        openModal
        shouldBlockBeforeUnload={false}
        title={options.title ?? ''}
        ariaLabel={options.title ? undefined : ariaLabel}
        message={message}
        variant="default"
        size="md"
        showCancelButton
        customConfirmLabel={options.okButtonText}
        customCancelLabel={options.cancelButtonText}
        handleClose={() => onSettle(false)}
        handleCancel={() => onSettle(false)}
        handleConfirm={() => onSettle(true)}
        testId="confirm-dialog"
        // callers may still have a legacy MUI dialog open underneath
        aboveOverlays
    />
);

/** Promise-based confirmation dialog; resolves true on confirm and false on cancel, Escape or outside click. */
export function confirm(__: Translator, message?: React.ReactNode, modalConfig?: ConfirmOptions): Promise<boolean> {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    return new Promise<boolean>((resolve) => {
        let settled = false;

        const settle = (confirmed: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            root.unmount();
            container.remove();
            resolve(confirmed);
        };

        root.render(
            <I18nContext.Provider value={{ __ }}>
                <ConfirmDialog
                    message={message}
                    ariaLabel={__('misc.confirm')}
                    options={{
                        okButtonText: modalConfig?.okButtonText ?? __('misc.confirm_yes'),
                        cancelButtonText: modalConfig?.cancelButtonText ?? __('misc.confirm_no'),
                        title: modalConfig?.title,
                    }}
                    onSettle={settle}
                />
            </I18nContext.Provider>,
        );
    });
}
